import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { expectedBehaviorFieldLabels } from "../../utils/testCaseExpectedLabels";
import { unwrapResponseText } from "../../utils/unwrapResponseText";

const FONT = "Lato, sans-serif";

const FLYOUT_PAPER_SX = {
  width: { xs: "100%", sm: 480, md: 520 },
  maxWidth: "100vw",
  bgcolor: "#FFFFFF",
  boxShadow: "-8px 0 32px rgba(16, 24, 40, 0.14)",
  boxSizing: "border-box",
};

const FOOTER_PRIMARY_BG =
  "linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const FOOTER_CANCEL_BG =
  "linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%), #F5F5F5";

const FLYOUT_FOOTER_SX = {
  flexShrink: 0,
  boxSizing: "border-box",
  width: "100%",
  minWidth: 0,
  minHeight: 88,
  px: { xs: 2, sm: "24px" },
  py: { xs: 2, sm: "24px" },
  bgcolor: "#F5F5F5",
  borderTop: "1px solid #E6E6E6",
  display: "flex",
  alignItems: { xs: "stretch", sm: "center" },
  justifyContent: "space-between",
  gap: { xs: 1.5, sm: "16px" },
  flexDirection: { xs: "column", sm: "row" },
};

const FLYOUT_FOOTER_TEXT_SX = {
  fontFamily: FONT,
  fontWeight: 400,
  fontSize: 13,
  lineHeight: "24px",
  letterSpacing: 0,
  color: "#555555",
  flex: { sm: "1 1 310px" },
  minWidth: 0,
};

const FLYOUT_FOOTER_BTN_TEXT_SX = {
  fontFamily: FONT,
  fontWeight: 700,
  fontStyle: "normal",
  fontSize: 15,
  lineHeight: "24px",
  letterSpacing: 0,
  textTransform: "none",
  verticalAlign: "middle",
};

/** Figma — Cancel: text button, no fill/border */
const FLYOUT_CANCEL_TEXT_BTN_SX = {
  ...FLYOUT_FOOTER_BTN_TEXT_SX,
  minWidth: 0,
  height: 40,
  minHeight: 40,
  px: "8px",
  py: "8px",
  borderRadius: "4px",
  color: "#555555",
  bgcolor: "transparent",
  boxShadow: "none",
  whiteSpace: "nowrap",
  "&:hover": {
    bgcolor: "transparent",
    color: "#383838",
    boxShadow: "none",
  },
  "&.Mui-disabled": {
    color: "#BBBBBB",
    bgcolor: "transparent",
  },
};

/** Figma — Edit: Secondary Large */
const FLYOUT_EDIT_BTN_SX = {
  ...FLYOUT_FOOTER_BTN_TEXT_SX,
  boxSizing: "border-box",
  height: 40,
  minHeight: 40,
  minWidth: 59,
  px: "16px",
  py: "8px",
  gap: "4px",
  borderRadius: "4px",
  border: "1px solid #BBBBBB",
  color: "#555555",
  backgroundColor: "#F5F5F5",
  backgroundImage: FOOTER_CANCEL_BG,
  boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
  whiteSpace: "nowrap",
  "&:hover": {
    borderColor: "#BBBBBB",
    backgroundColor: "#F5F5F5",
    backgroundImage: FOOTER_CANCEL_BG,
    color: "#555555",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
  },
};

/** Figma — Confirm: Primary Large */
const FLYOUT_PRIMARY_BTN_SX = {
  ...FLYOUT_FOOTER_BTN_TEXT_SX,
  boxSizing: "border-box",
  height: 40,
  minHeight: 40,
  minWidth: 87,
  px: "16px",
  py: "8px",
  gap: "4px",
  borderRadius: "4px",
  border: "1px solid transparent",
  color: "#FFFFFF",
  backgroundColor: "#005BF0",
  backgroundImage: FOOTER_PRIMARY_BG,
  boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.04)",
  whiteSpace: "nowrap",
  "&:hover:not(.Mui-disabled)": {
    backgroundColor: "#005BF0",
    backgroundImage: FOOTER_PRIMARY_BG,
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.04)",
    filter: "brightness(0.96)",
  },
  "&.Mui-disabled": {
    color: "#BBBBBB",
    bgcolor: "#F5F5F5",
    backgroundImage: "none",
    border: "1px solid #E6E6E6",
    opacity: 1,
  },
};

const FLYOUT_FOOTER_ACTIONS_SX = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: "8px",
  flexShrink: 0,
  width: { xs: "100%", sm: "auto" },
};

function getGroundTruthText(testCase) {
  const gt = testCase?.groundTruth || {};
  return unwrapResponseText(gt.expected_output || gt.expected_behavior || gt.pass_criteria || "");
}

function footerNoteForSource(source) {
  if (source === "Custom") return "Saved to Custom Test Strategies.";
  if (source === "Generated") return "Saved to Generated Test Strategies.";
  if (source === "Modified") return "Saved to Modified Test Strategies.";
  return "Saved to Test Strategies.";
}

function scoredAgainstLabel(testCase) {
  const name = (testCase?.metricName || "").trim();
  return name || "—";
}

/** Same outlined textarea as Add / Edit test-case flyouts, sized to this flyout. */
const TEXT_AREA_SX = {
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  "& .MuiOutlinedInput-root": {
    display: "flex",
    alignItems: "stretch",
    boxSizing: "border-box",
    width: "100%",
    maxWidth: "100%",
    height: "auto",
    minHeight: 51,
    overflow: "hidden",
    resize: "vertical",
    borderRadius: "4px",
    bgcolor: "#FFFFFF",
    opacity: 1,
    pl: "10px",
    pr: "10px",
    py: "8px",
    boxShadow: "inset 0px 2px 3px 1px #55555533",
    "& fieldset": {
      border: "1px solid #707070",
      pointerEvents: "none",
    },
    "&:hover fieldset": { border: "1px solid #707070" },
    "&.Mui-focused fieldset": { border: "1px solid #707070" },
  },
  "& .MuiOutlinedInput-input, & textarea": {
    boxSizing: "border-box",
    flex: 1,
    alignSelf: "stretch",
    width: "100% !important",
    maxWidth: "100%",
    height: "100% !important",
    minHeight: 0,
    margin: 0,
    py: 0,
    px: 0,
    fontFamily: FONT,
    fontWeight: 400,
    fontSize: 14,
    lineHeight: "21px",
    letterSpacing: 0,
    color: "#000000",
    resize: "none",
    overflow: "auto",
  },
  "& .MuiOutlinedInput-input::placeholder": {
    fontFamily: FONT,
    fontWeight: 400,
    fontSize: 14,
    lineHeight: "21px",
    letterSpacing: 0,
    color: "#707070",
    opacity: 1,
  },
};

const READ_ONLY_TEXTAREA_SX = {
  ...TEXT_AREA_SX,
  "& .MuiOutlinedInput-root": {
    ...TEXT_AREA_SX["& .MuiOutlinedInput-root"],
    bgcolor: "#F5F5F5",
    backgroundColor: "#F5F5F5",
    boxShadow: "none",
    resize: "none",
    "& fieldset": { border: "none" },
    "&:hover fieldset": { border: "none" },
    "&.Mui-focused fieldset": { border: "none" },
  },
};

const METADATA_FOOTER_SX = {
  display: "flex",
  flexDirection: { xs: "column", sm: "row" },
  alignItems: { xs: "stretch", sm: "flex-end" },
  justifyContent: "space-between",
  gap: "16px",
  width: "100%",
  minWidth: 0,
};

const SCORED_AGAINST_SX = {
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  minWidth: 0,
  alignItems: { xs: "flex-start", sm: "flex-end" },
  textAlign: { xs: "left", sm: "right" },
};

/** Figma — row confirm ground truth flyout (read-only open, Edit enables field). */
export default function ConfirmGroundTruthRowDialog({
  testCase,
  onClose,
  onConfirm,
  submitting,
  isContentGenerator = false,
}) {
  const open = Boolean(testCase);
  const [editMode, setEditMode] = useState(false);
  const [groundTruthText, setGroundTruthText] = useState("");
  const expectedLabels = expectedBehaviorFieldLabels(isContentGenerator);

  useEffect(() => {
    if (!testCase) {
      setEditMode(false);
      setGroundTruthText("");
      return;
    }
    setEditMode(false);
    setGroundTruthText(getGroundTruthText(testCase));
  }, [testCase?.id, open]);

  const canConfirm = groundTruthText.trim().length > 0;

  const handleConfirm = () => {
    if (!canConfirm || submitting) return;
    const existing = testCase?.groundTruth || {};
    onConfirm({
      expected_output: groundTruthText.trim(),
      pass_criteria: existing.pass_criteria || "",
      fail_criteria: "",
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={submitting ? undefined : onClose}
      PaperProps={{ sx: FLYOUT_PAPER_SX }}
      slotProps={{
        backdrop: {
          sx: { bgcolor: "rgba(52, 64, 84, 0.45)" },
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minWidth: 0 }}>
        <Box
          sx={{
            flexShrink: 0,
            boxSizing: "border-box",
            pl: { xs: 2, sm: "24px" },
            pr: { xs: 2, sm: "16px" },
            py: "16px",
            borderBottom: "1px solid #E6E6E6",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
          }}
        >
          <Typography
            sx={{
              fontFamily: FONT,
              fontWeight: 700,
              fontSize: 16,
              lineHeight: "24px",
              color: "#383838",
            }}
          >
            Confirm Ground Truth
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
            sx={{ width: 24, height: 24, p: 0, color: "#555555" }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            px: { xs: 2, sm: "24px" },
            py: { xs: 2, sm: "24px" },
            minWidth: 0,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", mb: 3, width: "100%", minWidth: 0 }}>
            <Typography
              component="label"
              htmlFor="confirm-ground-truth-field"
              sx={{
                fontFamily: FONT,
                fontWeight: 700,
                fontSize: 14,
                lineHeight: "21px",
                letterSpacing: 0,
                color: "#707070",
              }}
            >
              <Box component="span" sx={{ color: "#DE350B", mr: 0.25 }}>
                *
              </Box>
              {isContentGenerator ? expectedLabels.field : "Ground Truth"}
            </Typography>
            <TextField
              id="confirm-ground-truth-field"
              multiline
              fullWidth
              minRows={2}
              value={groundTruthText}
              onChange={(e) => {
                if (editMode) setGroundTruthText(e.target.value);
              }}
              disabled={submitting}
              InputProps={{ readOnly: !editMode }}
              sx={editMode ? TEXT_AREA_SX : READ_ONLY_TEXTAREA_SX}
            />
          </Box>

          <Box sx={METADATA_FOOTER_SX}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontWeight: 600,
                  fontSize: 12,
                  lineHeight: "18px",
                  color: "#707070",
                }}
              >
                SME Status
              </Typography>
              <Chip
                label="Not Validated"
                size="small"
                sx={{
                  height: 24,
                  borderRadius: "12px",
                  px: 1,
                  fontFamily: FONT,
                  fontWeight: 700,
                  fontSize: 13,
                  bgcolor: "#FEF3C7",
                  color: "#B45309",
                  width: "fit-content",
                }}
              />
            </Box>
            <Box sx={SCORED_AGAINST_SX}>
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontWeight: 600,
                  fontSize: 12,
                  lineHeight: "18px",
                  color: "#707070",
                }}
              >
                Scored against
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontWeight: 400,
                  fontSize: 14,
                  lineHeight: "21px",
                  color: "#555555",
                  wordBreak: "break-word",
                }}
              >
                {scoredAgainstLabel(testCase)}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={FLYOUT_FOOTER_SX}>
          <Typography sx={FLYOUT_FOOTER_TEXT_SX}>
            {footerNoteForSource(testCase?.source)}
          </Typography>
          <Box sx={FLYOUT_FOOTER_ACTIONS_SX}>
            <Button
              disableElevation
              disableRipple
              onClick={onClose}
              disabled={submitting}
              sx={FLYOUT_CANCEL_TEXT_BTN_SX}
            >
              Cancel
            </Button>
            {!editMode ? (
              <Button
                disableElevation
                onClick={() => setEditMode(true)}
                disabled={submitting}
                sx={FLYOUT_EDIT_BTN_SX}
              >
                Edit
              </Button>
            ) : null}
            <Button
              disableElevation
              variant="contained"
              onClick={handleConfirm}
              disabled={submitting || !canConfirm}
              sx={FLYOUT_PRIMARY_BTN_SX}
            >
              {submitting ? (
                <CircularProgress size={22} sx={{ color: "#fff" }} />
              ) : (
                "Confirm"
              )}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
